// Card refresh: re-run a pinned spec through the backend (no LLM) and get the
// same answer contract back. See POST /api/run-spec.
//
// Results are cached by spec (in memory, this session) so repeat interactions —
// toggling frequency back, re-opening a section, flipping compare on/off — are
// INSTANT instead of a round-trip. The demo windows are closed, so a cached series
// can't go stale; live/preliminary refreshes pass { fresh: true } to bypass it.

import type { Answer, Spec } from '../types'
import { apiFetch } from './http'
import { flagUnauthorized } from './auth'
import { isAnswer } from './contracts'
import { TENANT } from '../tenant'

const API_BASE = import.meta.env.VITE_API_URL ?? ''
const cache = new Map<string, Answer>()
export function clearRunSpecCache(): void { cache.clear() }

// MPD-84 N1: the server refused the spec under the supplier scope (403, code "out_of_scope"). A
// pinned card then shows "Not available in your scope" and drops its stored figures; every other
// failure keeps the last successful result.
export class OutOfScopeError extends Error {
  constructor() {
    super('This view is outside your permitted segments.')
    this.name = 'OutOfScopeError'
  }
}

export async function runSpec(spec: Spec, opts?: { fresh?: boolean; signal?: AbortSignal }): Promise<Answer> {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date())
  // MPD-81 S1: the supplier scope (an admin's "View as supplier") travels with the spec and the
  // cache key, so a preview gets the supplier's view; a real supplier's scope is set server-side.
  const manufacturer = TENANT.manufacturer || undefined
  const key = `${day}:${manufacturer ?? ''}:${JSON.stringify(spec)}`
  if (!opts?.fresh) {
    const hit = cache.get(key)
    if (hit) return hit
  }
  const r = await apiFetch(`${API_BASE}/api/run-spec`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ spec, fresh: !!opts?.fresh, ...(manufacturer ? { manufacturer } : {}) }),
    signal: opts?.signal ? AbortSignal.any([opts.signal, AbortSignal.timeout(90_000)]) : AbortSignal.timeout(90_000),
  })
  if (r.status === 401) flagUnauthorized()
  if (r.status === 403) {
    const body: unknown = await r.json().catch(() => null)
    if ((body as { code?: unknown } | null)?.code === 'out_of_scope') throw new OutOfScopeError()
  }
  if (!r.ok) throw new Error(`Could not update this view (HTTP ${r.status}).`)
  const ans: unknown = await r.json()
  if (!isAnswer(ans)) throw new Error('The updated chart could not be read. Please try again.')
  // Only cache a real, non-empty result. A transient MCP/cube blip comes back with
  // a null/empty chart; caching that would poison the series for the whole session
  // (numbers vanish and stay vanished). Re-fetching an empty is cheap.
  if (!ans.stale && (ans.chart?.data?.length ?? 0) > 0) cache.set(key, ans)
  return ans
}
